import { ChevronRight } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { EmptyBlock, Meter, SectionCard } from "../../components/primitives";
import { formatClock, formatCount, formatPercent } from "../../format";
import type { BranchMeta, FunnelBranch, PipelineSection } from "../../types";
import { BranchFlags, ProblemCount } from "./shared";

type Stage = "ordered" | "packed" | "captured" | "evaluated";
const STAGES: Stage[] = ["ordered", "packed", "captured", "evaluated"];

/** Recent orders followed pizza by pizza: ordered → packed → captured → evaluated, overall and per branch. */
export function FunnelCard({ data, branchOf }: { data: PipelineSection; branchOf: (id: string | null | undefined) => BranchMeta | null }) {
  const { t, tp } = useLocalization();
  const { funnel } = data;
  const totals = funnel.totals;
  const label = (stage: Stage) => t(`monitoring.pipeline.funnel.${stage}`);
  // red when a camera should have fired; amber when only app-trigger branches miss photos
  const uncapturedCritical = funnel.uncaptured.some((p) => p.bb_trigger) || funnel.uncaptured.length === 0;

  return (
    <SectionCard
      title={t("monitoring.pipeline.funnel.title")}
      description={t("monitoring.pipeline.funnel.description", { time: formatClock(funnel.since, false) })}
      actions={<span className="text-xs text-muted-foreground">{tp("monitoring.pipeline.ordersCount", funnel.orders)}</span>}
    >
      {totals.ordered === 0 ? (
        <EmptyBlock>{t("monitoring.pipeline.funnel.empty")}</EmptyBlock>
      ) : (
        <div className="flex flex-col gap-4">
          <ol className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {STAGES.map((stage, i) => {
              const count = totals[stage];
              const previous = i > 0 ? totals[STAGES[i - 1]] : null;
              const drop = previous !== null ? Math.max(0, previous - count) : 0;
              return (
                <li key={stage} className="relative min-w-0 rounded-lg border px-3 py-2.5">
                  {i > 0 && (
                    <ChevronRight
                      className="absolute -left-[13px] top-1/2 hidden h-4 w-4 -translate-y-1/2 rounded-full bg-card text-muted-foreground lg:block"
                      aria-hidden
                    />
                  )}
                  <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label(stage)}</div>
                  <div className="mt-0.5 flex items-baseline gap-2">
                    <span className="text-xl font-semibold tabular-nums">{formatCount(count)}</span>
                    <span className="text-[11px] tabular-nums text-muted-foreground">{formatPercent(count / totals.ordered)}</span>
                  </div>
                  <Meter value={count / totals.ordered} className="mt-1.5" />
                  <div className="mt-1.5 min-h-[1rem] text-[11px] leading-snug text-muted-foreground">
                    {i > 0 && drop > 0 && t(`monitoring.pipeline.funnel.drop.${stage as Exclude<Stage, "ordered">}`, { count: formatCount(drop) })}
                    {i > 0 && drop === 0 && t("monitoring.pipeline.funnel.noDrop")}
                  </div>
                  {stage === "captured" && totals.uncaptured > 0 && (
                    <div className={cn("mt-0.5 text-[11px] font-semibold leading-snug", uncapturedCritical ? "text-red-400" : "text-amber-300")}>
                      {tp("monitoring.pipeline.funnel.uncaptured", totals.uncaptured)}
                    </div>
                  )}
                </li>
              );
            })}
          </ol>

          <BranchFunnel branches={funnel.branches} branchOf={branchOf} />
        </div>
      )}
    </SectionCard>
  );
}

function BranchFunnel({ branches, branchOf }: { branches: FunnelBranch[]; branchOf: (id: string | null | undefined) => BranchMeta | null }) {
  const { t } = useLocalization();
  const name = (id: string | null) => branchOf(id)?.name || id || t("monitoring.unknownBranch");
  const columns: (keyof Omit<FunnelBranch, "id" | "uncaptured">)[] = ["ordered", "packed", "captured", "evaluated"];

  return (
    <>
      <div className="hidden md:block">
        <Table className="text-xs">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-8">{t("monitoring.col.branch")}</TableHead>
              {columns.map((c) => (
                <TableHead key={c} className="h-8 text-right">{t(`monitoring.pipeline.funnel.${c}`)}</TableHead>
              ))}
              <TableHead className="h-8 text-right" title={t("monitoring.pipeline.uncaptured.description")}>
                {t("monitoring.pipeline.funnel.uncapturedCol")}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {branches.map((b) => {
              const meta = branchOf(b.id);
              return (
                <TableRow key={b.id ?? "unknown"} className={cn(b.uncaptured > 0 && "bg-red-500/[0.04]")}>
                  <TableCell className="py-1.5">
                    <div className="break-words text-sm">{name(b.id)}</div>
                    {meta?.enable_bb_trigger === false && <BranchFlags acquisition={meta.enable_acquisition} bbTrigger={false} />}
                  </TableCell>
                  {columns.map((c) => (
                    <TableCell key={c} className="py-1.5 text-right font-mono tabular-nums">{formatCount(b[c])}</TableCell>
                  ))}
                  <TableCell className="py-1.5 text-right font-mono">
                    <ProblemCount value={b.uncaptured} tone={meta?.enable_bb_trigger === false ? "warn" : "nok"} />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <ul className="flex flex-col divide-y divide-border/60 rounded-md border md:hidden">
        {branches.map((b) => {
          const meta = branchOf(b.id);
          return (
            <li key={b.id ?? "unknown"} className="px-3 py-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="break-words text-sm">{name(b.id)}</div>
                  {meta?.enable_bb_trigger === false && <BranchFlags acquisition={meta.enable_acquisition} bbTrigger={false} />}
                </div>
                {b.uncaptured > 0 && (
                  <span className="shrink-0 text-xs">
                    <ProblemCount value={b.uncaptured} tone={meta?.enable_bb_trigger === false ? "warn" : "nok"} />{" "}
                    <span className="text-muted-foreground">{t("monitoring.pipeline.funnel.uncapturedCol")}</span>
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-1 text-[11px] text-muted-foreground">
                {columns.map((c, i) => (
                  <span key={c} className="whitespace-nowrap">
                    {i > 0 && <span aria-hidden>→ </span>}
                    {t(`monitoring.pipeline.funnel.${c}`)} <span className="font-mono tabular-nums text-foreground">{formatCount(b[c])}</span>
                  </span>
                ))}
              </div>
            </li>
          );
        })}
      </ul>
    </>
  );
}
